import { useQuery } from '@tanstack/react-query';
import { fetchReference } from '../../lib/lesson-api.js';
import { localName, t } from '../../lib/i18n.js';

/**
 * Projects by name, never by system identifier (FN-4).
 *
 * The QA/QC screens asked for a project's id, which no screen showed, and
 * listed records with no project at all. The list is the reader's own: the
 * reference endpoint narrows it to the projects their scope reaches.
 */

export function useProjects() {
  const reference = useQuery({
    queryKey: ['reference', 'qaqc'],
    queryFn: () => fetchReference('qaqc'),
    staleTime: 10 * 60_000,
  });
  const projects = reference.data?.projects ?? [];
  const byId = new Map(projects.map((project) => [project.id, project]));
  return {
    projects,
    loading: reference.isLoading,
    /** "ALPHA — Riyadh Tower", or a plain fallback while the list loads. */
    label: (id: string | null | undefined) => {
      if (!id) return 'No project';
      const project = byId.get(id);
      return project ? `${project.code} — ${localName(project)}` : t('Project');
    },
  };
}

export function ProjectPicker({
  id,
  value,
  onChange,
  className,
}: {
  id: string;
  value: string;
  onChange: (projectId: string) => void;
  className?: string;
}) {
  const { projects, loading } = useProjects();
  return (
    <select id={id} value={value} onChange={(event) => onChange(event.target.value)} className={className}>
      <option value="">{loading ? t("Loading projects…") : projects.length === 0 ? t("No projects you can use") : t("Choose a project")}</option>
      {projects.map((project) => (
        <option key={project.id} value={project.id}>
          {project.code} — {localName(project)}
        </option>
      ))}
    </select>
  );
}
